<template>
  <div v-if="location">
    <vital-accordion v-if="!weeklyViewOnly" v-slot="{toggle, isOpen, setContentRef}">
      <div class="flex flex-row laptop:inline-flex items-center justify-between" :class="{ 'cursor-pointer': allowOpen }" @click="toggle">
        <div :class="{ 'mr-10': allowOpen }">
          <template v-if="openToday">
            <template v-if="openNow">
              <span class="text-green-600 font-bold inline-block mr-5 after:pl-10 after:content-[url('../icons/breadcrumb-separator.svg')]">We are Open</span> Closes at {{ timeString(getCloseTime(today)) }}
            </template>
            <template v-else-if="openLater">
              <span class="font-bold inline-block mr-5">Closed</span> Opens {{ timeString(getOpenTime(today)) }}
            </template>
            <template v-else>
              <span class="font-bold inline-block mr-5">Closed</span> Opens {{ timeString(getOpenTime(nextOpenDay)) }} <template v-if="nextOpenDay === tomorrow">tomorrow</template><template v-else>{{ days[nextOpenDay].name }}</template>
            </template>
          </template>
          <template v-else>
            <span class="font-bold inline-block mr-5">Closed</span> Opens {{ timeString(getOpenTime(nextOpenDay)) }} <template v-if="nextOpenDay === tomorrow">tomorrow</template><template v-else>{{ days[nextOpenDay].name }}</template>
          </template>
        </div>
        <div v-if="allowOpen" class="transition-[transform]" :class="{ 'rotate-180' : isOpen }">
          <img :src="chevron" alt="Open">
        </div>
      </div>

      <div :ref="setContentRef" class="overflow-hidden transition-[height] h-0" v-show="allowOpen">
        <div v-for="day in days" :key="day.index" class="mb-5 last:mb-5 first:pt-15" :class="[day.index === today ? 'text-green-600 font-bold' : '' ]">
          {{ day.name }}
          <template v-if="openOnDay(day.index)">
            {{ timeString(getOpenTime(day.index)) }}&ndash;{{ timeString(getCloseTime(day.index)) }}
          </template>
          <template v-else>
            Closed
          </template>
        </div>
      </div>
    </vital-accordion>
    <div v-else >
      <div v-for="day in days" :key="day.index" class="mb-5 last:mb-0" :class="[day.index === today ? 'text-green-600 font-bold' : '' ]">
        {{ day.name }}
        <template v-if="openOnDay(day.index)">
          {{ timeString(getOpenTime(day.index)) }}&ndash;{{ timeString(getCloseTime(day.index)) }}
        </template>
        <template v-else>
          Closed
        </template>
      </div>
    </div>
  </div>
</template>
<script>
/*global google*/
/*eslint no-undef: "error"*/

import { ref, computed, onMounted } from 'vue'

import chevron from '../../icons/chevron.svg'

export default {
  props: {
    placeId: {
      type: String,
      required: true
    },
    allowOpen: {
      type: Boolean,
      default: true
    },
    weeklyViewOnly: {
      type: Boolean,
      default: false
    },
    hash: {
      type: String,
      required: true
    }
  },
  setup(props, {slots}) {
    const sessionKey = `location-${window.Shopify.shop}`
    const location = ref(null)
    const d = new Date();
    const nowHours = `${d.getHours()}`.padStart(2, '0')
    const nowMinutes = `${d.getMinutes()}`.padStart(2, '0')
    const now = `${nowHours}${nowMinutes}`
    const today = d.getDay();
    const tomorrow = (today + 1 === 7)? 0 : today + 1

    const days = [
      { index: 1, name: "Monday" },
      { index: 2, name: "Tuesday" },
      { index: 3, name: "Wednesday" },
      { index: 4, name: "Thursday" },
      { index: 5, name: "Friday" },
      { index: 6, name: "Saturday" },
      { index: 0, name: "Sunday" },
    ]

    onMounted(async () => {
      if (!hasLocationInStorage.value) {
        const url = `https://google-hours.vercel.app/api/ij/${props.placeId}?hash=${props.hash}`
        const response = await fetch(url)
        if (response.ok) {
          const data = await response.json()
          location.value = buildLocationFromPlace(data.place)
        }
        saveLocationToStorage(location.value);
      } else {
        location.value = loadLocationFromStorage();
      }
    })

    const hasLocationInStorage = computed(() => {
      return (window.sessionStorage.getItem(sessionKey) !== null);
    })

    const saveLocationToStorage = (location) => {
      window.sessionStorage.setItem(sessionKey, JSON.stringify(location));
    }

    const loadLocationFromStorage = () => {
      const locationJson = window.sessionStorage.getItem(sessionKey);
      return JSON.parse(locationJson);
    }

    const buildLocationFromPlace = (place) => {
      let components = place.address_components;
      return {
        address: {
          street: findAddressPart(components, 'street_number') + ' ' + findAddressPart(components, 'route'),
          city: findAddressPart(components, 'locality'),
          region: findAddressPart(components, 'administrative_area_level_1'),
          postal: findAddressPart(components, 'postal_code')
        },
        hours: place.opening_hours.periods,
        phone: place.formatted_phone_number
      }
    }

    const findAddressPart = (components, key) => {
      let part = components.filter(el => el.types.includes(key));
      if (part.length === 0) return null;
      return part[0].short_name;
    }

    const getOpenTime = (dayOfWeek) => {
      return location.value.hours.filter(el => el.open.day === dayOfWeek)[0].open;
    }

    const getCloseTime = (dayOfWeek) => {
      return location.value.hours.filter(el => el.close.day === dayOfWeek)[0].close;
    }

    const openOnDay = (dayOfWeek) => {
      return (location.value.hours.filter(el => el.open.day === dayOfWeek).length > 0);
    }

    const timeString = (timeData) => {
      const hours = parseInt(timeData.time.slice(0, 2))
      const minutes = timeData.time.slice(2)

      return ((hours > 12) ? hours - 12 : hours) +
          ((parseInt(minutes) > 0) ? ':' + minutes : '') +
          ((hours >= 12) ? ' PM' : ' AM');
    }

    const openToday = computed(() => {
      return openOnDay(today);
    })

    const openNow = computed(() => {
      return openToday.value && parseInt(now) >= parseInt(getOpenTime(today).time) && parseInt(now) < parseInt(getCloseTime(today).time);
    })

    const openLater = computed(() => {
      return openToday.value && parseInt(now) < parseInt(getOpenTime(today).time) ;
    })

    const nextOpenDay = computed(() => {
      let nextOpenDay = tomorrow;
      while (openOnDay(nextOpenDay) === false) {
        nextOpenDay = (nextOpenDay + 1 === 7)? 0 : nextOpenDay + 1;
      }
      return nextOpenDay;
    })

    return {
      location,
      getOpenTime,
      getCloseTime,
      openOnDay,
      openToday,
      openNow,
      openLater,
      timeString,
      days,
      today,
      tomorrow,
      nextOpenDay,
      chevron
    }
  }
}
</script>